import { useCallback, useMemo, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { INDENTATION_WIDTH, buildTree, flattenTree, getProjection, removeChildrenOf, } from '../utils.js';
export const useDragAndDrop = ({ items, onItemsChange, expandedItems, }) => {
    const [dragState, setDragState] = useState({
        activeId: null,
        overId: null,
        offsetLeft: 0,
    });
    const flattenedItems = useMemo(() => {
        const flattened = flattenTree(items);
        const collapsedItems = flattened.reduce((acc, { id, item }) => {
            if (item.children?.length && !expandedItems.has(id)) {
                return [...acc, id];
            }
            return acc;
        }, []);
        return removeChildrenOf(flattened, dragState.activeId
            ? [dragState.activeId, ...collapsedItems]
            : collapsedItems);
    }, [items, expandedItems, dragState.activeId]);
    const projection = useMemo(() => {
        const { activeId, overId, offsetLeft } = dragState;
        return activeId && overId
            ? getProjection(flattenedItems, activeId, overId, offsetLeft, INDENTATION_WIDTH)
            : null;
    }, [dragState, flattenedItems]);
    // Drag event handlers
    const handleDragStart = useCallback((event) => {
        setDragState((prev) => ({
            ...prev,
            activeId: event.active.id,
            overId: event.active.id,
            offsetLeft: 0,
        }));
    }, []);
    const handleDragMove = useCallback((event) => {
        setDragState((prev) => ({ ...prev, offsetLeft: event.delta.x }));
    }, []);
    const handleDragOver = useCallback((event) => {
        setDragState((prev) => ({
            ...prev,
            overId: event.over.id ?? null,
        }));
    }, []);
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        // Reset drag state
        setDragState({ activeId: null, overId: null, offsetLeft: 0 });
        if (!projection || !over || !onItemsChange)
            return;
        const { depth, parentId } = projection;
        const clonedItems = [...flattenTree(items)];
        const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
        const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
        if (overIndex === -1 || activeIndex === -1)
            return;
        const activeTreeItem = clonedItems[activeIndex];
        // update item with new depth and parent
        clonedItems[activeIndex] = {
            ...activeTreeItem,
            depth,
            parentId,
            // todo: might not need the or root
            item: { ...activeTreeItem.item, parentId: parentId || 'root' },
        };
        // reorder items and rebuild tree
        const reorderedItems = arrayMove(clonedItems, activeIndex, overIndex);
        const newTree = buildTree(reorderedItems);
        onItemsChange(newTree);
    }, [projection, items, onItemsChange]);
    const handleDragCancel = useCallback(() => {
        setDragState({ activeId: null, overId: null, offsetLeft: 0 });
    }, []);
    return {
        dragState,
        flattenedItems,
        handlers: {
            onDragStart: handleDragStart,
            onDragMove: handleDragMove,
            onDragOver: handleDragOver,
            onDragEnd: handleDragEnd,
            onDragCancel: handleDragCancel,
        },
        sortableIds: flattenedItems.map(({ id }) => id),
    };
};
